import map from "lodash/map";
import slice from "lodash/slice";
import { options } from "../constants/challengeConstants";

// Helper function to intelligently merge tasks
const mergeTasks = (
  existingTasks = [],
  videoLink,
  activities = [],
  section = ""
) => {
  const existingTaskLabels = existingTasks.map((task) => task.label);
  const newTasks = [];

  // Get all possible activity task labels based on current activities
  const currentActivityLabels = activities
    .map((activity) => {
      const matchingOption = options.find(
        (option) => option.value === activity || option.value === activity.value
      );
      return matchingOption ? matchingOption.label : null;
    })
    .filter(Boolean);

  // Determine the appropriate video task label based on context
  let expectedVideoTaskLabel = null;
  if (videoLink) {
    if (section === "welcome") {
      expectedVideoTaskLabel = "Watch the welcome video";
    } else if (section === "ending") {
      expectedVideoTaskLabel = "Watch the ending video";
    } else {
      expectedVideoTaskLabel = "Watch today's video";
    }
  }

  // Filter existing tasks to only keep those that should still exist
  const filteredTasks = existingTasks.filter((task) => {
    const taskLabel = task.label || task;

    // Keep manually created tasks (those without _id are likely generated)
    if (!task._id) {
      return true;
    }

    // Keep video tasks only if videoLink exists and task matches expected label
    if (taskLabel.includes("video")) {
      return videoLink && taskLabel === expectedVideoTaskLabel;
    }

    // Keep activity tasks only if they match current activities
    return currentActivityLabels.includes(taskLabel);
  });

  // Add video task if videoLink exists and no matching task exists
  if (
    videoLink &&
    !filteredTasks.some((task) => (task.label || task).includes("video"))
  ) {
    newTasks.push({ label: expectedVideoTaskLabel });
  }

  // Add activity tasks for activities that don't have matching tasks
  activities.forEach((activity) => {
    const matchingOption = options.find(
      (option) => option.value === activity || option.value === activity.value
    );
    if (
      matchingOption &&
      !filteredTasks.some(
        (task) => (task.label || task) === matchingOption.label
      )
    ) {
      newTasks.push({ label: matchingOption.label });
    }
  });

  // Preserve filtered existing tasks
  const preservedTasks = filteredTasks.map((task) => ({
    _id: task._id,
    label: task.label || task,
    passed: task.passed,
  }));

  return [...preservedTasks, ...newTasks];
};

export const computeWelcome = (welcomeItem) => {
  const computedWelcome = {
    ...welcomeItem,
    tasks: mergeTasks(
      welcomeItem.tasks,
      welcomeItem.videoLink,
      welcomeItem.activities,
      "welcome"
    ),
    habits: (welcomeItem.habits || []).map((habit) => ({
      _id: habit._id, // Preserve the _id field
      title: habit.title || habit,
      type: habit.type || "text",
      description: habit.description || "",
      options: habit.options || [],
    })),
  };
  return computedWelcome;
};

export const computeEnding = (endingItem) => {
  const computedEnding = {
    ...endingItem,
    habits: (endingItem.habits || []).map((habit) => ({
      _id: habit._id, // Preserve the _id field
      title: habit.title || habit,
      type: habit.type || "text",
      description: habit.description || "",
      options: habit.options || [],
    })),
    tasks: mergeTasks(
      endingItem.tasks,
      endingItem.videoLink,
      endingItem.activities,
      "ending"
    ),
  };
  return computedEnding;
};

export const computeSchedule = (scheduleItems, duration) => {
  const computedSchedule = slice(scheduleItems, 0, duration).map(
    (item, index) => {
      const computedItem = {
        ...item,
        startDay: index + 1,
        endDay: index + 1,
        tasks: mergeTasks(
          item.tasks,
          item.videoLink,
          item.activities,
          "schedule"
        ),
        habits: (item.habits || []).map((habit) => ({
          _id: habit._id, // Preserve the _id field
          title: habit.title || habit,
          type: habit.type || "text",
          description: habit.description || "",
          options: habit.options || [],
        })),
      };
      return computedItem;
    }
  );
  return computedSchedule;
};

export const createChallenge = ({
  title,
  description,
  difficulty,
  duration,
  timedChallenge,
  startDate,
  challengeType,
  commitment,
  imageLink,
  welcomeItem,
  endingItem,
  scheduleItems,
}) => {
  const computedWelcome = computeWelcome(welcomeItem);
  const computedEnding = computeEnding(endingItem);
  const computedSchedule = computeSchedule(scheduleItems, duration);

  return {
    title,
    description,
    difficulty,
    duration,
    startDate: timedChallenge ? startDate : null,
    type: challengeType?.value || challengeType,
    commitment,
    imageLink,
    welcomeItem: computedWelcome,
    endingItem: computedEnding,
    schedule: computedSchedule,
  };
};
